import { Routes, Route, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useSmoothScroll } from '@/hooks/useSmoothScroll';
import { isLowPowerEnvironment } from '@/utils/performance';
import { initI18n } from './i18n';

const Header = lazy(() => import('@/components/sections/Header').then((m) => ({ default: m.Header })));
const LandingPage = lazy(() => import('@/pages/Landing').then((m) => ({ default: m.LandingPage })));
const TalkshowPage = lazy(() => import('@/pages/Talkshow').then((m) => ({ default: m.TalkshowPage })));

const AboutPage = lazy(() => import('@/pages/About').then((module) => ({ default: module.AboutPage })));
const VsNotionPage = lazy(() => import('@/pages/VsNotion').then((module) => ({ default: module.VsNotionPage })));
const BoardingPage = lazy(() => import('@/pages/Boarding').then((module) => ({ default: module.BoardingPage })));
const DashboardPage = lazy(() => import('@/pages/Dashboard').then((m) => ({ default: m.DashboardPage })));
const EnginePage = lazy(() => import('@/pages/Engine').then((m) => ({ default: m.Engine })));

// Boot i18n once for the whole app shell
initI18n();

const NavigateToExternal = ({ url }: { url: string }) => {
  useEffect(() => {
    window.location.replace(url);
  }, [url]);
  return null;
};

function App() {
  const [lowPowerMode, setLowPowerMode] = useState(false);
  const location = useLocation();

  useSmoothScroll(lowPowerMode);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const updateMode = () => {
      setLowPowerMode(isLowPowerEnvironment());
    };

    updateMode();
    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', updateMode);
    } else {
      reducedMotion.addListener(updateMode);
    }

    return () => {
      if (typeof reducedMotion.removeEventListener === 'function') {
        reducedMotion.removeEventListener('change', updateMode);
      } else {
        reducedMotion.removeListener(updateMode);
      }
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('low-power-mode', lowPowerMode);
    return () => {
      document.documentElement.classList.remove('low-power-mode');
    };
  }, [lowPowerMode]);

  const isOSPage = location.pathname.startsWith('/boarding') || location.pathname.startsWith('/dashboard');

  return (
    <>
      {!lowPowerMode ? <div className="noise-overlay" /> : null}
      <Suspense fallback={null}>
        {!isOSPage && <Header />}
        <Routes>
          <Route 
            path="/" 
            element={<LandingPage />} 
          />
          <Route 
            path="/talkshow" 
            element={<TalkshowPage />} 
          />
          <Route
            path="/pricing"
            element={<NavigateToExternal url="https://apps.orqon.com/app/services" />}
          />
          <Route
            path="/about"
            element={<AboutPage />}
          />
          <Route
            path="/vs-notion"
            element={<VsNotionPage />}
          />
          <Route
            path="/boarding"
            element={<BoardingPage />}
          />
          <Route
            path="/dashboard"
            element={<DashboardPage />}
          />
          <Route
            path="/engine"
            element={<EnginePage />}
          />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
